import styled, {
  applySizeToImageUrl,
  Button,
  ButtonVariant,
  css,
  Device,
  IconName,
  IFalconTheme,
  Image,
  ImageSizeVariant,
  ResponsiveTypography,
  ResponsiveTypographyVariant,
  Scale,
  StyledComponent,
  TooltipPosition,
  useDeviceViewport,
} from '@se-admin/falcon';
import { i18n } from '@secret-escapes/i18n';

import * as React from 'react';

import { SaleCardVariant } from './SaleCard.interfaces';

import { IAffiliateDetails } from '../../common/contexts/ApplicationContext';
import { ITranslationObject } from '../../common/types';
import { IPhoto, ISale, ITerritoryConfiguration } from '../../common/types/apiSchema';
import { Color } from '../../common/types/color';
import { LoadingSkeletonSection } from '../../common/utils/loadingSkeletons.styles';
import { FeatureFlags } from '../../constants/featureFlags';
import { useFeatureFlag } from '../../utils';
import { CountdownVariant } from '../Countdown/Countdown';
import { DiscountBadgeVariant } from '../DiscountBadge/DiscountBadge.interfaces';
import { DiscountBadge } from '../DiscountBadge/index';
import { PriceValueVariant } from '../PriceValue/PriceValue.interfaces';
import { getSaleLabelConfiguration } from '../SaleLabel/SaleLabel.helpers';
import SaleLabelList, { SaleLabelVariant, TSaleLabelRenderPropsArray } from '../SaleLabelList';
import Countdown from './../Countdown';
import HeartIcon from './../HeartIcon';
import {
  ButtonContainer,
  ContentContainer,
  CustomIcon,
  CustomPriceValue,
  CustomSmallImage,
  EndMessageDesktop,
  HeartContainer,
  ImageContainer,
  MobileContentContainer,
  MobileTitleTextWrapper,
  NormalDiscountContainer,
  PriceBadgeContainer,
  PriceDiscountBadgeContainer,
  SaleLabelsContainer,
  SaleLabelsContainerDesktop,
  SaleLabelsContainerSmall,
  UrgentDiscountContainer,
} from './SaleCard.styles';
import { AspectRatioWrapper } from './SaleCardSkeleton/SaleCardSkeleton.styles';
import {
  bookingsMadeStyle,
  descriptionStyle,
  DestinationText,
  DestinationWrapper,
  peopleViewingStyle,
  SaleTitle,
} from './SaleText/SaleText.styles';
import {
  getNumberOfBookings,
  getPeopleViewing,
  POPULAR_THRESHOLD,
} from './SaleText/SaleText.utils';
import {
  getDiscountBadge,
  getPriceValue,
  IDiscountBadgeConfigurationOutput,
  IPriceValueConfigurationOutput,
} from './utils/pricesHelper';

export { renderContent, renderHeartIcon, renderLabels, renderSaleImage };

interface IPeopleViewingProps {
  peopleViewing: number;
}

const PeopleViewing: StyledComponent<
  typeof ResponsiveTypography,
  IFalconTheme,
  IPeopleViewingProps
> = styled(ResponsiveTypography)(({ peopleViewing }: IPeopleViewingProps) => {
  return css`
    ${peopleViewingStyle}
    && {
      font-weight: ${peopleViewing > POPULAR_THRESHOLD ? '600' : 'normal'};
    }
  `;
});

/**
 * Renders content
 */
const renderContent: (
  sale: ISale,
  variant: SaleCardVariant,
  userId: string,
  affiliateDetails: IAffiliateDetails,
  daysToEnd: number,
  endDate: Date,
  territoryConfig: ITerritoryConfiguration,
  isFlightFunctionalityEnabled: boolean,
  translations: ITranslationObject,
  showTooltip?: boolean,
  theme?: IFalconTheme
) => JSX.Element = (
  sale,
  variant,
  userId,
  affiliateDetails,
  daysToEnd,
  endDate,
  territoryConfig,
  isFlightFunctionalityEnabled,
  translations,
  showTooltip,
  theme
) => {
  const isNormalVariant: boolean = variant === SaleCardVariant.normal;
  const discountBadge: IDiscountBadgeConfigurationOutput = getDiscountBadge(
    sale,
    variant,
    showTooltip
  );
  const isTimeLimited: boolean = sale.attributes && sale.attributes.isTimeLimited;
  const isUpcoming: boolean =
    sale.dates && sale.dates.start && new Date(sale.dates.start).getTime() > new Date().getTime();

  const deviceViewport: Device = useDeviceViewport();
  const showSaleCardUrgency: boolean = !useFeatureFlag(FeatureFlags.disableSaleCardUrgency);

  const currentVisitors: number = sale.bookingStats ? sale.bookingStats.currentVisitors : null;
  const peopleViewing: string = getPeopleViewing(currentVisitors, translations);
  const numberOfBookings: string = getNumberOfBookings(sale, translations);

  const Description: StyledComponent<typeof ResponsiveTypography, IFalconTheme, {}> = styled(
    ResponsiveTypography
  )`
    ${descriptionStyle}
  `;

  const BookingsMade: StyledComponent<typeof ResponsiveTypography, IFalconTheme, {}> = styled(
    ResponsiveTypography
  )`
    ${bookingsMadeStyle}
  `;

  return (
    <React.Fragment>
      {isTimeLimited && renderEndMessage(variant, daysToEnd, endDate)}
      <ContentContainer variant={variant}>
        <div>
          {(deviceViewport !== Device.mobile || variant === SaleCardVariant.small) && (
            <React.Fragment>
              <DestinationWrapper saleCardVariant={variant}>
                {variant === SaleCardVariant.small && (
                  <CustomIcon name={IconName.marker} fillColor={theme.color.blackText} />
                )}
                <DestinationText
                  saleCardVariant={variant}
                  variant={ResponsiveTypographyVariant.micro}
                >
                  {sale.editorial.destinationName || ''}
                </DestinationText>
              </DestinationWrapper>
              <SaleTitle saleCardVariant={variant} variant={ResponsiveTypographyVariant.bodyBold}>
                {sale.editorial.title}
              </SaleTitle>
              {variant === SaleCardVariant.normal && (
                <React.Fragment>
                  <Description
                    variant={ResponsiveTypographyVariant.micro}
                    allowDangerousRender={true}
                  >
                    {sale.editorial.reasonToLove}
                  </Description>
                </React.Fragment>
              )}
            </React.Fragment>
          )}

          {isNormalVariant && (
            <MobileContentContainer>
              <ResponsiveTypography variant={ResponsiveTypographyVariant.body}>
                <React.Fragment>
                  <MobileTitleTextWrapper>
                    <b>{`${sale.editorial.title} • `}</b>
                    <span>{`${sale.editorial.destinationName}${isTimeLimited ? ' • ' : ''}`}</span>
                  </MobileTitleTextWrapper>
                  {isTimeLimited && getEndMessageElement(daysToEnd, endDate, variant)}
                </React.Fragment>
              </ResponsiveTypography>
            </MobileContentContainer>
          )}
          {isNormalVariant &&
            renderLabels(
              sale,
              isFlightFunctionalityEnabled,
              territoryConfig,
              SaleLabelVariant.normal,
              false,
              translations
            )}
          {!(variant === SaleCardVariant.small) &&
            renderLabels(
              sale,
              isFlightFunctionalityEnabled,
              territoryConfig,
              SaleLabelVariant.normal,
              true,
              translations
            )}
          {variant === SaleCardVariant.normal && showSaleCardUrgency && (
            <React.Fragment>
              {peopleViewing && (
                <PeopleViewing
                  variant={ResponsiveTypographyVariant.micro}
                  peopleViewing={currentVisitors}
                >
                  {peopleViewing}
                </PeopleViewing>
              )}
              {numberOfBookings && (
                <BookingsMade variant={ResponsiveTypographyVariant.micro} color={Color.red}>
                  {numberOfBookings}
                </BookingsMade>
              )}
            </React.Fragment>
          )}
        </div>
        {discountBadge.showUrgencyBadge &&
          renderUrgentDiscountBadge(variant, discountBadge, isTimeLimited)}
        {renderPriceAndDiscountBadge(
          sale,
          variant,
          userId,
          affiliateDetails,
          discountBadge,
          translations,
          isUpcoming,
          showTooltip
        )}
      </ContentContainer>
    </React.Fragment>
  );
};

/**
 * Renders HeartIcon
 */
const renderHeartIcon: (
  sale: ISale,
  variant: SaleCardVariant,
  baseUrl: string,
  favouriteSales: string[]
) => JSX.Element = (sale, variant, baseUrl, favouriteSales) => {
  const checked: boolean = favouriteSales && favouriteSales.includes(sale.id);

  return (
    <HeartContainer variant={variant}>
      <HeartIcon baseUrl={baseUrl} checked={checked} saleId={sale.id} />
    </HeartContainer>
  );
};

const SaleCardImageSize: {
  [v in SaleCardVariant]: { [d in Device]: { height: number; width: number } };
} = {
  [SaleCardVariant.normal]: {
    [Device.mobile]: { width: 518, height: 360 },
    [Device.tablet]: { width: 400, height: 278 },
    [Device.desktop]: { width: 400, height: 278 },
  },
  [SaleCardVariant.small]: {
    [Device.mobile]: { width: 413, height: 275 },
    [Device.tablet]: { width: 315, height: 210 },
    [Device.desktop]: { width: 315, height: 210 },
  },
};

/**
 * Renders Sale Image
 */
const renderSaleImage: (
  sale: ISale,
  variant: SaleCardVariant,
  isFlightFunctionalityEnabled: boolean,
  territoryConfig: ITerritoryConfiguration,
  translations: ITranslationObject,
  onImageLoad: () => void,
  device: Device
) => JSX.Element = (
  sale,
  variant,
  isFlightFunctionalityEnabled,
  territoryConfig,
  translations,
  onImageLoad,
  device
) => {
  const image: IPhoto = sale.photos && sale.photos[0];

  const size: { width: number; height: number } = SaleCardImageSize[variant][device];
  let imageUrl: string = '';
  if (image) {
    imageUrl = image.urlWithSize
      ? applySizeToImageUrl(image.urlWithSize, size.width, size.height)
      : image.url;
  }

  return (
    <ImageContainer variant={variant}>
      {image &&
        (variant === SaleCardVariant.small ? (
          <CustomSmallImage
            alt={image.caption}
            objectFit={'cover'}
            size={ImageSizeVariant.full}
            srcSet={[{ src: imageUrl }]}
            onLoad={onImageLoad}
          />
        ) : (
          <Image
            fallback={
              <AspectRatioWrapper>
                <LoadingSkeletonSection
                  marginBottom={Scale.m}
                  width="100%"
                  height="100%"
                  background={true}
                  borderRadius={`${Scale.xxs} ${Scale.xxs} 0 0`}
                />
              </AspectRatioWrapper>
            }
            onLoad={onImageLoad}
            alt={image.caption}
            size={ImageSizeVariant.fluid}
            srcSet={[{ src: imageUrl }]}
          />
        ))}
      {variant === SaleCardVariant.small &&
        renderLabels(
          sale,
          isFlightFunctionalityEnabled,
          territoryConfig,
          SaleLabelVariant.small,
          true,
          translations
        )}
    </ImageContainer>
  );
};

/**
 * Renders End Message
 */
const renderEndMessage: (
  variant: SaleCardVariant,
  daysToEnd: number,
  endDate: Date
) => JSX.Element = (variant, daysToEnd, endDate) => {
  return (
    <EndMessageDesktop saleCardVariant={variant}>
      <React.Fragment>
        {variant !== SaleCardVariant.normal && (
          <CustomIcon name={IconName.time} fillColor={Color.white} />
        )}
        {getEndMessageElement(daysToEnd, endDate, variant)}
      </React.Fragment>
    </EndMessageDesktop>
  );
};

/**
 * Renders labels
 */
const renderLabels: (
  sale: ISale,
  isFlightFunctionalityEnabled: boolean,
  territoryConfig: ITerritoryConfiguration,
  labelVariant: SaleLabelVariant,
  desktop: boolean,
  translations: ITranslationObject
) => JSX.Element = (
  sale,
  isFlightFunctionalityEnabled,
  territoryConfig,
  labelVariant,
  desktop,
  translations
) => {
  const saleLabelProperties: TSaleLabelRenderPropsArray = sale.attributes
    ? getSaleLabelConfiguration(
        sale,
        translations,
        isFlightFunctionalityEnabled,
        territoryConfig.flightsAvailableAtCSPage,
        territoryConfig.depositAvailableAtCSPage,
        territoryConfig.isZeroDepositDisplayable,
        labelVariant
      )
    : null;

  return labelVariant === SaleLabelVariant.small ? (
    <SaleLabelsContainerSmall>
      {saleLabelProperties && (
        <SaleLabelList saleLabelListProperties={saleLabelProperties} variant={labelVariant} />
      )}
    </SaleLabelsContainerSmall>
  ) : desktop ? (
    <SaleLabelsContainerDesktop>
      {saleLabelProperties && (
        <SaleLabelList saleLabelListProperties={saleLabelProperties} variant={labelVariant} />
      )}
    </SaleLabelsContainerDesktop>
  ) : (
    <SaleLabelsContainer>
      {saleLabelProperties && (
        <SaleLabelList saleLabelListProperties={saleLabelProperties} variant={labelVariant} />
      )}
    </SaleLabelsContainer>
  );
};

/**
 * Renders urgent discount badge
 */
const renderUrgentDiscountBadge: (
  variant: SaleCardVariant,
  discountBadge: IDiscountBadgeConfigurationOutput,
  isTimeLimited: boolean
) => JSX.Element = (variant, discountBadge, isTimeLimited) => {
  return (
    <UrgentDiscountContainer variant={variant} isTimeLimited={isTimeLimited}>
      <DiscountBadge
        variant={DiscountBadgeVariant.urgent}
        showTooltip={discountBadge.showTooltip}
        discountPrice={discountBadge.discountValue}
        discountTooltip={discountBadge.discountTooltip}
      />
    </UrgentDiscountContainer>
  );
};

/**
 * Renders price and discountBadge
 */
const renderPriceAndDiscountBadge: (
  sale: ISale,
  variant: SaleCardVariant,
  userId: string,
  affiliateDetails: IAffiliateDetails,
  discountBadge: IDiscountBadgeConfigurationOutput,
  translations: ITranslationObject,
  isUpcoming: boolean,
  showTooltip?: boolean
) => JSX.Element = (
  sale,
  variant,
  userId,
  affiliateDetails,
  discountBadge,
  translations,
  isUpcoming,
  showTooltip = true
) => {
  const priceConfiguration: IPriceValueConfigurationOutput = getPriceValue(
    sale,
    userId,
    affiliateDetails
  );

  const isNormalVariant: boolean = variant === SaleCardVariant.normal;

  return (
    <PriceBadgeContainer variant={variant}>
      <PriceDiscountBadgeContainer>
        {priceConfiguration.showPriceValue && (
          <CustomPriceValue
            {...priceConfiguration.priceValueProperties}
            tooltipPosition={isNormalVariant ? TooltipPosition.bottom : TooltipPosition.top}
            variant={isNormalVariant ? PriceValueVariant.normal : PriceValueVariant.small}
            showTooltip={
              showTooltip &&
              priceConfiguration.priceValueProperties.translationData &&
              priceConfiguration.priceValueProperties.translationData.leadRateTooltip
                ? true
                : false
            }
          />
        )}

        {discountBadge.showRegularBadge && (
          <NormalDiscountContainer>
            <DiscountBadge
              variant={isNormalVariant ? DiscountBadgeVariant.regular : DiscountBadgeVariant.small}
              showTooltip={
                discountBadge.showTooltip && discountBadge.discountTooltip ? true : false
              }
              discountPrice={discountBadge.discountValue}
              discountTooltip={discountBadge.discountTooltip}
            />
          </NormalDiscountContainer>
        )}
      </PriceDiscountBadgeContainer>

      {variant === SaleCardVariant.normal && (
        <ButtonContainer>
          <Button variant={ButtonVariant.primary} onClick={onClick}>
            {i18n(
              isUpcoming ? 'sale.preview.text' : 'urgency.seeAvailability.label',
              [],
              translations
            )}
          </Button>
        </ButtonContainer>
      )}
    </PriceBadgeContainer>
  );
};

/**
 * Returns the jsx element containin the end message depending on the remaining time until the sale expires.
 */
const getEndMessageElement: (
  daysToEnd: number,
  endDate: Date,
  variant: SaleCardVariant
) => JSX.Element | string = (daysToEnd, endDate, variant) => {
  let messageColor: Color;
  let countdownVariant: CountdownVariant = CountdownVariant.FULL;

  if (variant === SaleCardVariant.small) {
    messageColor = Color.white;
    countdownVariant = CountdownVariant.SHORT;
  } else if (daysToEnd < 1) {
    messageColor = Color.red;
  }

  return <Countdown endDate={endDate} color={messageColor} variant={countdownVariant} />;
};

/**
 * onClick in button is mandatory but because the whole sale card takes the click, this doesn't
 * need to do anything.
 */
/*istanbul ignore next */
const onClick: () => void = () => {
  return;
};
